import { ReactNode, useCallback, useLayoutEffect, useRef, useState } from 'react'

/**
 * The material-kit stylesheet keys the floating label, the focus colour and the
 * animated underline off classes and a marker span that Material Kit's script
 * adds with jQuery. The SPA loads no jQuery, so this owns the same state:
 * without `is-empty` every `label-floating` label stays in its floated position,
 * because the rule is written as `label-floating:not(.is-empty)`.
 */
export function FormGroup({
  className = '',
  children,
  style,
}: {
  className?: string
  children: ReactNode
  style?: React.CSSProperties
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [isEmpty, setIsEmpty] = useState(true)
  const [isFocused, setIsFocused] = useState(false)

  const syncEmptiness = useCallback(() => {
    const control = ref.current?.querySelector<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
      'input, select, textarea',
    )
    setIsEmpty(control === null || control === undefined || String(control.value ?? '') === '')
  }, [])

  // The controls are controlled components, so any value change re-renders this
  // and re-reads the control rather than needing the value threaded in as a prop.
  useLayoutEffect(syncEmptiness)

  const classes = [className, isEmpty ? 'is-empty' : '', isFocused ? 'is-focused' : ''].filter((part) => part !== '').join(' ')

  return (
    <div
      ref={ref}
      className={classes}
      style={style}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onInput={syncEmptiness}
    >
      {children}
      <span className="material-input" />
    </div>
  )
}
